import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { AppThunk } from '../../store';
import axios from 'axios';
import * as fromAuth from './../../auth/state';
import get from 'lodash.get';
import cloneDeep from 'lodash.clonedeep';
import {
  ListItem,
  BiomarkerData,
  UserSubmittedLabResultsListResponse,
  UserSubmittedLabResultsDetailsResponse,
  UserSubmittedLabResultsRequest,
} from '../../../interfaces/user-submitted-lab-result';
import { getAccessToken } from './../../auth/state';

const BASE_API = process.env['REACT_APP_HUM_ADMIN_API_URL'] ?? ' ';
const instance = axios.create({
  baseURL: BASE_API,
  headers: {
    Accept: 'application/json',
    'Content-Type': 'application/json',
  },
});

interface UserSubmittedLabResultsState {
  list: ListItem[];
  paginationKeys: string[];
  page: number;
  nextPageToken: string | null;
  loadingCurrent: boolean;
  loadingBiomarker: boolean;
  listError: string | null;
  current: UserSubmittedLabResultsDetailsResponse | null;
  currentError: string | null;
  factor: number;
  filter: string;
  disableNext: boolean;
  biomarkers: BiomarkerData;
  biomarkerError: string | null;
  savedUserSubmittedLabResultsDetails: UserSubmittedLabResultsRequest;
  savingSuccessful: boolean;
  automatedStatusChange: boolean;
  shouldRedirect: boolean;
}

const initialState: UserSubmittedLabResultsState = {
  list: [],
  paginationKeys: [''],
  page: 0,
  nextPageToken: null,
  loadingCurrent: false,
  loadingBiomarker: false,
  listError: null,
  current: null,
  currentError: null,
  factor: 0,
  filter: 'toDo',
  disableNext: false,
  biomarkers: {
    biomarkers: {},
    rejectReasons: [],
  },
  biomarkerError: null,
  savedUserSubmittedLabResultsDetails: {
    status: '',
    laboratoryName: '',
    sampleDate: '',
    rejectedReason: '',
    rejectedComment: '',
    items: [],
  },
  savingSuccessful: false,
  automatedStatusChange: false,
  shouldRedirect: false,
};

export const userSubmittedLabResultsSlice = createSlice({
  name: 'userSubmittedLabResults',
  initialState,
  reducers: {
    startLoading: state => {
      state.loadingCurrent = true;
      state.disableNext = true;
    },
    startLoadingBiomarker: state => {
      state.loadingBiomarker = true;
    },
    loadUserSubmittedLabResultsSuccess: (
      state,
      action: PayloadAction<UserSubmittedLabResultsListResponse>
    ) => {
      state.loadingCurrent = false;
      if (action.payload && action.payload.data.length !== 0) {
        if (state.factor === 1) {
          state.page++;
        } else if (state.factor === -1) {
          state.page--;
        }
        if (state.factor !== -1 && !state.paginationKeys.includes(action.payload.nextPageToken)) {
          state.paginationKeys.push(action.payload.nextPageToken);
        }
        state.list = action.payload.data;
      }
      state.disableNext = !action.payload.nextPageToken;
      state.listError = '';
    },
    clearCurrent: state => {
      state.current = null;
      state.currentError = '';
      state.paginationKeys = [];
      state.page = 0;
    },
    clearPagination: state => {
      state.filter = 'toDo';
      state.current = null;
      state.paginationKeys = [];
      state.page = 0;
    },
    loadUserSubmittedLabResultsError: (state, action: PayloadAction<string>) => {
      state.loadingCurrent = false;
      state.listError = action.payload;
    },
    loadUserSubmittedLabResultByIdSuccess: (
      state,
      action: PayloadAction<UserSubmittedLabResultsDetailsResponse>
    ) => {
      state.loadingCurrent = false;
      state.current = action.payload;
      state.currentError = '';
    },
    loadUserSubmittedLabResultByIdError: (state, action: PayloadAction<string>) => {
      state.loadingCurrent = false;
      state.currentError = action.payload;
    },
    setFactor: (state, action: PayloadAction<number>) => {
      state.factor = action.payload;
    },
    setFilter: (state, action: PayloadAction<string>) => {
      state.paginationKeys = [''];
      state.page = 0;
      state.nextPageToken = null;
      state.list = [];
      state.factor = 0;
      state.filter = action.payload;
    },
    loadBiomarkerConfigSuccess: (state, action: PayloadAction<BiomarkerData>) => {
      state.loadingBiomarker = false;
      state.biomarkers = action.payload;
      state.currentError = '';
    },
    loadBiomarkerConfigError: (state, action: PayloadAction<string>) => {
      state.loadingBiomarker = false;
      state.biomarkerError = action.payload;
    },
    updateUserSubmittedLabResultByIdSuccess: (
      state,
      action: PayloadAction<UserSubmittedLabResultsDetailsResponse>
    ) => {
      state.loadingCurrent = false;
      state.current = cloneDeep(action.payload);
      state.savingSuccessful = true;
      state.shouldRedirect = true;
      state.currentError = '';
    },
    updateUserSubmittedLabResultByIdError: (state, action: PayloadAction<string>) => {
      state.loadingCurrent = false;
      state.savingSuccessful = false;
      state.currentError = action.payload;
    },
    automatedStatusChange: (state, action: PayloadAction<boolean>) => {
      state.automatedStatusChange = action.payload;
    },
    savingSuccessfulChange: (state, action: PayloadAction<boolean>) => {
      state.savingSuccessful = action.payload;
      state.currentError = '';
    },
    shouldRedirectChange: (state, action: PayloadAction<boolean>) => {
      state.shouldRedirect = action.payload;
    },
  },
  extraReducers: {
    [fromAuth.logoutSuccess.type]: state => (state = initialState),
  },
});

export const {
  startLoading,
  startLoadingBiomarker,
  loadUserSubmittedLabResultsSuccess,
  loadUserSubmittedLabResultsError,
  loadUserSubmittedLabResultByIdSuccess,
  loadUserSubmittedLabResultByIdError,
  loadBiomarkerConfigSuccess,
  loadBiomarkerConfigError,
  clearCurrent,
  setFactor,
  setFilter,
  updateUserSubmittedLabResultByIdSuccess,
  updateUserSubmittedLabResultByIdError,
  automatedStatusChange,
  savingSuccessfulChange,
  shouldRedirectChange,
} = userSubmittedLabResultsSlice.actions;

export const loadUserSubmittedLabResults =
  (nextPage?: boolean, prevPage?: boolean): AppThunk =>
  async (dispatch, getState) => {
    dispatch(startLoading());
    const pageNumber = getState().userSubmittedLabResults.page;
    let paginationKey = '';
    if (nextPage) {
      paginationKey = getState().userSubmittedLabResults.paginationKeys[pageNumber + 1];
      dispatch(setFactor(1));
    } else if (prevPage) {
      dispatch(setFactor(-1));
      if (pageNumber - 1 > 0) {
        paginationKey = getState().userSubmittedLabResults.paginationKeys[pageNumber - 1];
      }
    }
    try {
      const accessToken = await getAccessToken();
      let filter = getState().userSubmittedLabResults.filter;
      const url = `lab-results/user-submitted/filter/${filter}`;
      if (accessToken) {
        const response = await instance.get<UserSubmittedLabResultsListResponse>(url, {
          params: {
            ...(paginationKey ? { nextPageToken: paginationKey } : {}),
          },
          headers: {
            Authorization: `Bearer ${accessToken.getJwtToken()}`,
          },
        });
        dispatch(loadUserSubmittedLabResultsSuccess(response.data));
        return response.data;
      } else {
        dispatch(loadUserSubmittedLabResultsError('Missing token'));
      }
    } catch (e) {
      dispatch(loadUserSubmittedLabResultsError(get(e, 'response.data.error', (e as any).message)));
    }
  };

export const loadUserSubmittedLabResultById =
  (userId: string, resultId: string): AppThunk =>
  async dispatch => {
    dispatch(startLoading());
    dispatch(clearCurrent());
    try {
      const accessToken = await getAccessToken();
      if (accessToken) {
        const response = await instance.get<UserSubmittedLabResultsDetailsResponse>(
          `lab-results/user-submitted/detail/${userId}/${resultId}`,
          {
            headers: {
              Authorization: `Bearer ${accessToken.getJwtToken()}`,
            },
          }
        );
        dispatch(loadUserSubmittedLabResultByIdSuccess(response.data));
      } else {
        dispatch(loadUserSubmittedLabResultByIdError('Missing token'));
      }
    } catch (e) {
      dispatch(
        loadUserSubmittedLabResultByIdError(get(e, 'response.data.error', (e as any).message))
      );
    }
  };

export const loadBiomarkersConfig = (): AppThunk => async dispatch => {
  dispatch(startLoadingBiomarker());
  try {
    const accessToken = await getAccessToken();
    if (accessToken) {
      const response = await instance.get<BiomarkerData>(`lab-results/user-submitted/config`, {
        headers: {
          Authorization: `Bearer ${accessToken.getJwtToken()}`,
        },
      });
      dispatch(loadBiomarkerConfigSuccess(response.data));
    } else {
      dispatch(loadBiomarkerConfigError('Missing token'));
    }
  } catch (e) {
    dispatch(loadBiomarkerConfigError(get(e, 'response.data.error', (e as any).message)));
  }
};

export const updateUserSubmittedLabResultById =
  (
    userId: string,
    resultId: string,
    updatedUserSubmittedLabResult: UserSubmittedLabResultsRequest
  ): AppThunk =>
  async (dispatch, getState) => {
    try {
      const accessToken = await getAccessToken();
      if (accessToken) {
        const response = await instance.patch<UserSubmittedLabResultsDetailsResponse>(
          `lab-results/user-submitted/${userId}/${resultId}`,
          updatedUserSubmittedLabResult,
          {
            headers: {
              Authorization: `Bearer ${accessToken.getJwtToken()}`,
            },
          }
        );
        dispatch(updateUserSubmittedLabResultByIdSuccess(response.data));
      } else {
        dispatch(updateUserSubmittedLabResultByIdError('Missing token'));
      }
    } catch (e) {
      dispatch(
        updateUserSubmittedLabResultByIdError(get(e, 'response.data.message', (e as any).message))
      );
    }
  };

export const setAutomatedStatusChange =
  (status: boolean): AppThunk =>
  async dispatch => {
    dispatch(automatedStatusChange(status));
  };

export const setShouldRedirect =
  (status: boolean): AppThunk =>
  async dispatch => {
    dispatch(shouldRedirectChange(status));
  };

export const setSavingSuccessful =
  (successfulChange: boolean): AppThunk =>
  async dispatch => {
    dispatch(savingSuccessfulChange(successfulChange));
  };

export const clearPagination = (): AppThunk => async dispatch => {
  dispatch(clearPagination());
};

export default userSubmittedLabResultsSlice.reducer;
